/**
 * i18n Initialization
 *
 * Architecture:
 * - Namespace-based (one JSON per feature per language)
 * - Browser language detection with localStorage persistence
 * - Translations bundled inline (imported as webpack modules)
 * - French (fr) as default/fallback language
 * - English (en) as first additional language
 *
 * To add a new language:
 * 1. Create `src/i18n/locales/{lang}/` folder
 * 2. Add all namespace JSON files (copy from `fr/`)
 * 3. Add the language code to `supportedLngs` below
 * 4. Import the JSON files below and add to `resources`
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// ── French translations ────────────────────────────────────────────────────
import fr_common from './locales/fr/common.json';
import fr_validation from './locales/fr/validation.json';
import fr_errors from './locales/fr/errors.json';
import fr_notifications from './locales/fr/notifications.json';
import fr_navigation from './locales/fr/navigation.json';
import fr_auth from './locales/fr/auth.json';
import fr_dashboard from './locales/fr/dashboard.json';
import fr_patients from './locales/fr/patients.json';
import fr_consultations from './locales/fr/consultations.json';
import fr_pharmacy from './locales/fr/pharmacy.json';
import fr_caisse from './locales/fr/caisse.json';
import fr_imagerie from './locales/fr/imagerie.json';
import fr_technique from './locales/fr/technique.json';
import fr_admin from './locales/fr/admin.json';
import fr_cpn from './locales/fr/cpn.json';
import fr_bed_management from './locales/fr/bed_management.json';
import fr_statistics from './locales/fr/statistics.json';
import fr_results from './locales/fr/results.json';
import fr_profile from './locales/fr/profile.json';

// ── English translations ────────────────────────────────────────────────────
import en_common from './locales/en/common.json';
import en_validation from './locales/en/validation.json';
import en_errors from './locales/en/errors.json';
import en_notifications from './locales/en/notifications.json';
import en_navigation from './locales/en/navigation.json';
import en_auth from './locales/en/auth.json';
import en_dashboard from './locales/en/dashboard.json';
import en_patients from './locales/en/patients.json';
import en_consultations from './locales/en/consultations.json';
import en_pharmacy from './locales/en/pharmacy.json';
import en_caisse from './locales/en/caisse.json';
import en_imagerie from './locales/en/imagerie.json';
import en_technique from './locales/en/technique.json';
import en_admin from './locales/en/admin.json';
import en_cpn from './locales/en/cpn.json';
import en_bed_management from './locales/en/bed_management.json';
import en_statistics from './locales/en/statistics.json';
import en_results from './locales/en/results.json';
import en_profile from './locales/en/profile.json';

i18n
    // Detect browser/stored language automatically
    .use(LanguageDetector)
    // Pass i18n instance to react-i18next
    .use(initReactI18next)
    .init({
        // ── Bundled resources ─────────────────────────────────────────────────
        resources: {
            fr: {
                common: fr_common,
                validation: fr_validation,
                errors: fr_errors,
                notifications: fr_notifications,
                navigation: fr_navigation,
                auth: fr_auth,
                dashboard: fr_dashboard,
                patients: fr_patients,
                consultations: fr_consultations,
                pharmacy: fr_pharmacy,
                caisse: fr_caisse,
                imagerie: fr_imagerie,
                technique: fr_technique,
                admin: fr_admin,
                cpn: fr_cpn,
                bed_management: fr_bed_management,
                statistics: fr_statistics,
                results: fr_results,
                profile: fr_profile,
            },
            en: {
                common: en_common,
                validation: en_validation,
                errors: en_errors,
                notifications: en_notifications,
                navigation: en_navigation,
                auth: en_auth,
                dashboard: en_dashboard,
                patients: en_patients,
                consultations: en_consultations,
                pharmacy: en_pharmacy,
                caisse: en_caisse,
                imagerie: en_imagerie,
                technique: en_technique,
                admin: en_admin,
                cpn: en_cpn,
                bed_management: en_bed_management,
                statistics: en_statistics,
                results: en_results,
                profile: en_profile,
            },
        },

        // ── Language Settings ─────────────────────────────────────────────────
        fallbackLng: 'fr',
        supportedLngs: ['fr', 'en'],
        defaultNS: 'common',

        // ── Language Detection ────────────────────────────────────────────────
        detection: {
            // Detection order: localStorage → navigator → htmlTag
            order: ['localStorage', 'navigator', 'htmlTag'],
            // Persist language choice in localStorage under key 'i18nextLng'
            caches: ['localStorage'],
            lookupLocalStorage: 'i18nextLng',
        },

        // ── Interpolation ─────────────────────────────────────────────────────
        interpolation: {
            escapeValue: false,
        },

        // ── React Settings ────────────────────────────────────────────────────
        react: {
            // No async loading — all translations bundled, no Suspense needed
            useSuspense: false,
        },

        // ── Debug (disable in production) ─────────────────────────────────────
        debug: process.env.NODE_ENV === 'development',
    });

export default i18n;
